import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { EMAIL_RE, fieldClass } from "@/lib/validation";

function validate(email, password) {
  const errs = {};
  if (!email.trim())                    errs.email    = "E-Mail-Adresse ist erforderlich.";
  else if (!EMAIL_RE.test(email.trim())) errs.email   = "Bitte eine gültige E-Mail-Adresse eingeben.";
  if (!password)                        errs.password = "Passwort ist erforderlich.";
  return errs;
}

export default function AdminLogin() {
  const { login, user, isAuthenticated } = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const [email,     setEmail]    = useState("");
  const [password,  setPassword] = useState("");
  const [touched,   setTouched]  = useState({ email: false, password: false });
  const [submitted, setSubmitted] = useState(false);
  const [apiError,  setApiError]  = useState(null);
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      const to = user.must_change_password
        ? "/admin/force-change"
        : (location.state?.from || "/admin/dashboard");
      navigate(to, { replace: true });
    }
  }, [isAuthenticated, user, location.state, navigate]);

  const errs = validate(email, password);
  const show = (f) => (submitted || touched[f]) && !!errs[f];

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setApiError(null);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      navigate(
        u.must_change_password ? "/admin/force-change" : (location.state?.from || "/admin/dashboard"),
        { replace: true },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Login fehlgeschlagen.";
      if (detail === "invalid_credentials")                msg = "E-Mail oder Passwort falsch.";
      else if (detail?.error === "too_many_login_attempts") msg = detail.message_de;
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-porcelain px-5 py-12" data-testid="page-admin-login">
      <div className="relative w-full max-w-md">
        <div aria-hidden="true" className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-roseMilk/70 blur-3xl" />
        <div className="relative bg-white border border-sandBeige rounded-3xl shadow-soft p-8">
          <p className="font-serif text-cocoa text-2xl lowercase">
            womenintech<span className="text-raspberry">.life</span>
          </p>
          <p className="text-xs text-warmGrey mb-7">Admin · Phase 4</p>
          <h1 className="font-serif text-3xl text-cocoa">Willkommen zurück.</h1>
          <p className="text-sm text-warmGrey mt-2">
            Logge dich ein, um Submissions, Investor:innen und das Dealflow-Backend zu verwalten.
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-7 flex flex-col gap-4" data-testid="admin-login-form">
            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="login-email" className="text-xs font-medium text-cocoa">
                E-Mail <span className="text-raspberry">*</span>
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setApiError(null); }}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                className={fieldClass("wit-input", show("email"), !!email.trim() && !errs.email)}
                data-testid="admin-login-email"
              />
              {show("email") && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle size={12} />{errs.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="login-pw" className="text-xs font-medium text-cocoa">
                Passwort <span className="text-raspberry">*</span>
              </label>
              <input
                id="login-pw"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setApiError(null); }}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                className={fieldClass("wit-input", show("password"), !!password && !errs.password)}
                data-testid="admin-login-password"
              />
              {show("password") && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle size={12} />{errs.password}
                </p>
              )}
            </div>

            {apiError && (
              <p className="text-sm text-coral flex items-center gap-2" role="alert" data-testid="admin-login-error">
                <AlertCircle size={14} /> {apiError}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="wit-btn-primary disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {loading ? "Wird geprüft …" : <>Einloggen <ArrowRight size={16} /></>}
            </button>
            <Link
              to="/admin/forgot-password"
              className="text-xs text-warmGrey hover:text-raspberry text-center mt-2"
              data-testid="admin-login-forgot"
            >
              Passwort vergessen?
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
}
